import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import './DomainInventory.css';

/* ─── Types ──────────────────────────────────────────────── */

type AutoRunStatus = 'gathering' | 'needs_review' | 'removing' | 'completed' | 'failed';

type InfraType = 'GOOGLE' | 'MICROSOFT';

interface AutoRemovalRun {
  id: string;
  status: AutoRunStatus;
  infra_type: InfraType;
  domain_removal_run_id: string | null;
  records_due: number;
  records_eligible: number;
  records_quarantined: number;
  domains_total: number;
  records_removed: number;
  records_failed: number;
  cap_exceeded: boolean;
  error_message: string | null;
  created_at: string;
  finished_at: string | null;
}

type RecordOutcome = 'eligible' | 'quarantined' | 'removed' | 'failed';

interface AutoRemovalRecord {
  id: string;
  auto_run_id: string;
  airtable_record_id: string;
  new_sheet_url: string | null;
  team_comment: string | null;
  domains: string[];
  domain_source: 'sheet' | 'clean_domains' | null;
  clean_vs_sheet_diff: { only_in_sheet: string[]; only_in_clean: string[] } | null;
  outcome: RecordOutcome;
  reason: string | null;
}

interface AutoRunDetail {
  run: AutoRemovalRun | null;
  records: AutoRemovalRecord[];
  removal: { users_deleted: number; domains_deleted: number } | null;
}

interface MsItem {
  domain_name: string;
  source_record_id: string | null;
  tenants_found?: number | null;
  users_deleted?: number | null;
  domain_deleted: boolean;
  default_domain_blocked: boolean;
  status: string;
  error: string | null;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

interface AutoRemovalConfig {
  enabled: boolean;
  max_domains: number;
  run_hour: number;
  run_minute: number;
  timezone: string;
  infra_type?: InfraType;
  last_run_date: string | null;
  updated_at: string;
  updated_by: string | null;
}

/* ─── Constants ──────────────────────────────────────────── */

const INFLIGHT_STATUSES: ReadonlySet<AutoRunStatus> = new Set(['gathering', 'removing']);
const AIRTABLE_RECORD_BASE =
  'https://airtable.com/appIe9RGyH4b9YtzH/tblRDtgPxq4rdWXNm';

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '-';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

/** "HH:MM" string for an <input type="time"> from hour/minute. */
function toTimeValue(hour: number, minute: number): string {
  const h = String(Math.max(0, Math.min(23, hour))).padStart(2, '0');
  const m = String(Math.max(0, Math.min(59, minute))).padStart(2, '0');
  return `${h}:${m}`;
}

/** "01:00 PM" style label for the collapsed summary. */
function toTime12hLabel(hour: number, minute: number): string {
  const h = Math.max(0, Math.min(23, hour));
  const m = Math.max(0, Math.min(59, minute));
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

/** Parse "HH:MM" back to { hour, minute }; null if malformed. */
function fromTimeValue(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

function statusBadgeClass(status: AutoRunStatus): string {
  switch (status) {
    case 'gathering':
    case 'removing':
      return 'cln-badge--running';
    case 'needs_review':
      return 'cln-badge--honey';
    case 'completed':
      return 'cln-badge--completed';
    case 'failed':
      return 'cln-badge--failed';
    default:
      return 'cln-badge--muted';
  }
}

/** Distinct-but-on-palette badge per infra: GOOGLE amber, MICROSOFT blue. */
function infraBadgeClass(infra: InfraType): string {
  return infra === 'GOOGLE' ? 'cln-badge--honey' : 'cln-badge--queued';
}

function outcomeBadgeClass(outcome: RecordOutcome): string {
  switch (outcome) {
    case 'eligible':
      return 'cln-badge--queued';
    case 'quarantined':
      return 'cln-badge--honey';
    case 'removed':
      return 'cln-badge--success';
    case 'failed':
      return 'cln-badge--error';
    default:
      return 'cln-badge--muted';
  }
}

const OUTCOME_RANK: Record<RecordOutcome, number> = {
  failed: 0,
  quarantined: 1,
  eligible: 2,
  removed: 3,
};

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const linkStyle: React.CSSProperties = { color: 'var(--blue)', textDecoration: 'none' };

/* ─── View modes ─────────────────────────────────────────── */

type View = { mode: 'list' } | { mode: 'detail'; runId: string; infra: InfraType };

type InfraFilter = 'ALL' | InfraType;

/* ─── Main component ─────────────────────────────────────── */

export function AutoRemovals(): JSX.Element {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [runs, setRuns] = useState<AutoRemovalRun[] | null>(null);
  const [filter, setFilter] = useState<InfraFilter>('ALL');
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const qs = filter === 'ALL' ? '' : `?infra=${filter}`;
      const data = await api<AutoRemovalRun[]>(`/api/cleanup/auto-removal/runs${qs}`);
      setRuns(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [filter, showToast]);

  useEffect(() => {
    if (view.mode === 'list') void refreshList();
  }, [view.mode, refreshList]);

  /* List polling: 7s while inflight, 15s otherwise. */
  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (view.mode !== 'list') {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
      return;
    }
    const hasInflight = runs?.some((r) => INFLIGHT_STATUSES.has(r.status));
    const intervalMs = hasInflight ? 7000 : 15000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [view.mode, runs, refreshList]);

  if (view.mode === 'detail') {
    return (
      <RunDetailView
        runId={view.runId}
        infra={view.infra}
        onBack={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onError={(msg) => showToast('error', msg)}
        toast={toast}
      />
    );
  }

  return (
    <RunsListView
      runs={runs}
      filter={filter}
      onFilterChange={setFilter}
      onOpen={(runId, infra) => setView({ mode: 'detail', runId, infra })}
      onRefresh={() => void refreshList()}
      onToast={showToast}
      toast={toast}
    />
  );
}

/* ─── List view ──────────────────────────────────────────── */

function RunsListView({
  runs,
  filter,
  onFilterChange,
  onOpen,
  onRefresh,
  onToast,
  toast,
}: {
  runs: AutoRemovalRun[] | null;
  filter: InfraFilter;
  onFilterChange: (f: InfraFilter) => void;
  onOpen: (runId: string, infra: InfraType) => void;
  onRefresh: () => void;
  onToast: (type: Toast['type'], message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const filterOptions: { value: InfraFilter; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'GOOGLE', label: 'Google' },
    { value: 'MICROSOFT', label: 'Microsoft' },
  ];
  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <div className="cln-header-row">
            <div>
              <h1 className="cln-title">Auto Removals</h1>
              <p className="cln-subtitle">
                History of automated, Airtable-driven removal runs and their per-record decisions.
              </p>
            </div>
          </div>
        </header>

        <ConfigCard infra="GOOGLE" onToast={onToast} onRunStarted={onRefresh} />
        <ConfigCard infra="MICROSOFT" onToast={onToast} onRunStarted={onRefresh} />

        <section className="cln-card">
          <div className="cln-card-header">
            <h2 className="cln-card-title">
              Runs <span className="cln-cell-faint">({runs?.length ?? 0})</span>
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Filter runs by infra">
                {filterOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`cln-btn ${
                      filter === opt.value ? 'cln-btn--primary' : 'cln-btn--ghost'
                    }`}
                    onClick={() => onFilterChange(opt.value)}
                    aria-pressed={filter === opt.value}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh list">
                <RefreshIcon />
              </button>
            </div>
          </div>

          {runs === null && <div className="cln-empty">Loading...</div>}

          {runs !== null && runs.length === 0 && (
            <div className="cln-empty">
              <h3 className="cln-empty-title">No runs yet</h3>
              <p className="cln-empty-text">
                Auto-removal runs are triggered by the daily cron (or a manual &ldquo;Run now&rdquo;).
                They will appear here once one has started.
              </p>
            </div>
          )}

          {runs !== null && runs.length > 0 && (
            <table className="cln-table">
              <thead>
                <tr>
                  <th className="cln-col-date">Started</th>
                  <th>Infra</th>
                  <th>Status</th>
                  <th className="cln-col-num">Due</th>
                  <th className="cln-col-num">Eligible</th>
                  <th className="cln-col-num">Quarantined</th>
                  <th className="cln-col-num">Removed</th>
                  <th className="cln-col-num">Failed</th>
                  <th className="cln-col-num">Domains</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="cln-row" onClick={() => onOpen(r.id, r.infra_type)}>
                    <td className="cln-col-date cln-cell-faint">{relativeTime(r.created_at)}</td>
                    <td>
                      <span className={`cln-badge ${infraBadgeClass(r.infra_type)}`}>
                        {r.infra_type}
                      </span>
                    </td>
                    <td>
                      <span className={`cln-badge ${statusBadgeClass(r.status)}`}>{r.status}</span>
                      {r.cap_exceeded && (
                        <span className="cln-badge cln-badge--honey" style={{ marginLeft: 6 }}>
                          NEEDS REVIEW
                        </span>
                      )}
                    </td>
                    <td className="cln-col-num cln-num--ink">{r.records_due}</td>
                    <td className="cln-col-num">{r.records_eligible}</td>
                    <td className="cln-col-num">{r.records_quarantined}</td>
                    <td className="cln-col-num">{r.records_removed}</td>
                    <td className="cln-col-num">
                      {r.records_failed > 0 ? (
                        <span className="cln-num-detail">{r.records_failed}</span>
                      ) : (
                        r.records_failed
                      )}
                    </td>
                    <td className="cln-col-num">{r.domains_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Configuration card (list view, above runs) ─────────── */

function ConfigCard({
  infra,
  onToast,
  onRunStarted,
}: {
  infra: InfraType;
  onToast: (type: Toast['type'], message: string) => void;
  onRunStarted: () => void;
}): JSX.Element {
  const [cfg, setCfg] = useState<AutoRemovalConfig | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [maxDomains, setMaxDomains] = useState('');
  const [timeValue, setTimeValue] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [starting, setStarting] = useState(false);

  const infraLabel = infra === 'MICROSOFT' ? 'Microsoft' : 'Google';

  const applyConfig = useCallback((c: AutoRemovalConfig) => {
    setCfg(c);
    setEnabled(c.enabled);
    setMaxDomains(String(c.max_domains));
    setTimeValue(toTimeValue(c.run_hour, c.run_minute));
  }, []);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await api<AutoRemovalConfig>(
        `/api/cleanup/auto-removal/config?infra=${infra}`,
      );
      applyConfig(data);
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to load config');
    }
  }, [infra, applyConfig, onToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = useCallback(async (): Promise<void> => {
    const parsedTime = fromTimeValue(timeValue);
    if (!parsedTime) {
      onToast('error', 'Daily run time must be a valid HH:MM.');
      return;
    }
    const cap = Number(maxDomains);
    if (!Number.isInteger(cap) || cap < 1) {
      onToast('error', 'Domain cap must be a whole number of at least 1.');
      return;
    }
    setSaving(true);
    try {
      const updated = await api<AutoRemovalConfig>('/api/cleanup/auto-removal/config', {
        method: 'PUT',
        body: JSON.stringify({
          infra,
          enabled,
          max_domains: cap,
          run_hour: parsedTime.hour,
          run_minute: parsedTime.minute,
        }),
      });
      applyConfig(updated);
      onToast('success', `${infraLabel} auto-removal config saved.`);
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  }, [infra, infraLabel, enabled, maxDomains, timeValue, applyConfig, onToast]);

  /* runNow: POSTs { infra } so the right per-infra tick fires. */
  const runNow = useCallback(async (): Promise<void> => {
    setStarting(true);
    try {
      const res = await fetch('/api/cleanup/auto-removal/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ infra }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        onToast('error', body?.error?.message ?? 'Failed to start run.');
        return;
      }
      onRunStarted();
    } catch {
      onToast('error', 'Network error - could not start run.');
    } finally {
      setStarting(false);
    }
  }, [infra, onToast, onRunStarted]);

  const tz = cfg?.timezone ?? 'Asia/Dubai';

  return (
    <section className="cln-card">
      <div
        className="cln-card-header cln-accordion-header"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <h2 className="cln-card-title">
          <span className="cln-accordion-chevron" aria-hidden>
            {expanded ? '⌃' : '⌄'}
          </span>
          <span className={`cln-badge ${infraBadgeClass(infra)}`} style={{ marginRight: 8 }}>
            {infra}
          </span>
          {infraLabel} configuration
          {infra === 'MICROSOFT' && (
            <span className="cln-accordion-summary">· removals only</span>
          )}
          {cfg && (
            <span className="cln-accordion-glance">
              <span
                className={`cln-badge ${cfg.enabled ? 'cln-badge--success' : 'cln-badge--muted'}`}
              >
                {cfg.enabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <span className="cln-accordion-time">
                Daily {toTime12hLabel(cfg.run_hour, cfg.run_minute)} {cfg.timezone}
              </span>
              <span className="cln-accordion-summary">· Cap {cfg.max_domains}</span>
            </span>
          )}
        </h2>
        <button
          type="button"
          className="cln-btn cln-btn--primary"
          onClick={(e) => {
            e.stopPropagation();
            void runNow();
          }}
          disabled={starting}
        >
          {starting ? 'Starting...' : 'Run now'}
        </button>
      </div>

      {!expanded ? null : cfg === null ? (
        <div className="cln-empty">Loading config...</div>
      ) : (
        <div className="cln-config">
          {infra === 'MICROSOFT' && (
            <div className="cln-config-meta cln-cell-faint" style={{ marginBottom: 8 }}>
              Microsoft is removals-only: this run deletes tenant users and domains. There is no
              Microsoft provisioning/licensing path here.
            </div>
          )}
          <label className="cln-config-toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>
              <span className="cln-config-label">Daily auto-run enabled</span>
              <span className="cln-config-hint">
                When OFF, the daily cron is skipped - only the manual &ldquo;Run now&rdquo; works.
              </span>
            </span>
          </label>

          <div className="cln-config-field">
            <span className="cln-config-label">Domain cap</span>
            <input
              type="number"
              min={1}
              className="cln-search-input cln-config-input"
              value={maxDomains}
              onChange={(e) => setMaxDomains(e.target.value)}
            />
            <span className="cln-config-hint">
              Batches over this many domains HOLD for review instead of deleting.
            </span>
          </div>

          <div className="cln-config-field">
            <span className="cln-config-label">
              Daily run time <span className="cln-cell-faint">({tz})</span>
            </span>
            <input
              type="time"
              className="cln-search-input cln-config-input"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
            />
          </div>

          <div className="cln-config-actions">
            <button
              type="button"
              className="cln-btn cln-btn--primary"
              onClick={() => void onSave()}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="cln-config-meta cln-summary-meta">
            Last run date: {cfg.last_run_date ?? '-'} · updated {relativeTime(cfg.updated_at)} by{' '}
            {cfg.updated_by ?? '-'}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Detail view ────────────────────────────────────────── */

function RunDetailView({
  runId,
  infra,
  onBack,
  onError,
  toast,
}: {
  runId: string;
  infra: InfraType;
  onBack: () => void;
  onError: (msg: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [detail, setDetail] = useState<AutoRunDetail | null>(null);
  const [msItems, setMsItems] = useState<MsItem[] | null>(null);

  const refreshDetail = useCallback(async (): Promise<void> => {
    try {
      const data = await api<AutoRunDetail>(`/api/cleanup/auto-removal/runs/${runId}`);
      setDetail(data);
      if (infra === 'MICROSOFT') {
        const ms = await api<{ items: MsItem[] }>(
          `/api/cleanup/auto-removal/runs/${runId}/ms-items`,
        );
        setMsItems(ms.items);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [runId, infra, onError]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <button type="button" className="cln-back" onClick={onBack}>
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
        </header>
        {detail ? (
          <RunDetailBody
            detail={detail}
            infra={infra}
            msItems={msItems}
            onRefresh={() => void refreshDetail()}
          />
        ) : (
          <div className="cln-loading">Loading run...</div>
        )}
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function RunDetailBody({
  detail,
  infra,
  msItems,
  onRefresh,
}: {
  detail: AutoRunDetail;
  infra: InfraType;
  msItems: MsItem[] | null;
  onRefresh: () => void;
}): JSX.Element {
  const { run, records, removal } = detail;
  const isMicrosoft = infra === 'MICROSOFT';

  const sortedRecords = [...records].sort((a, b) => {
    const r = OUTCOME_RANK[a.outcome] - OUTCOME_RANK[b.outcome];
    if (r !== 0) return r;
    return a.airtable_record_id.localeCompare(b.airtable_record_id);
  });

  return (
    <>
      <section className="cln-summary-card">
        <div className="cln-summary-top">
          <div>
            <h2 className="cln-summary-title">
              <span className={`cln-badge ${infraBadgeClass(infra)}`} style={{ marginRight: 8 }}>
                {infra}
              </span>
              Auto run{' '}
              {run && <span className="cln-cell-mono cln-cell-faint">{run.id.slice(0, 8)}</span>}
            </h2>
            {run && (
              <div className="cln-summary-meta">
                {relativeTime(run.created_at)}
                {run.finished_at && <> · ran {durationLabel(run.created_at, run.finished_at)}</>}
                {run.domain_removal_run_id && (
                  <>
                    {' · '}
                    <span className="cln-cell-mono">
                      removal {run.domain_removal_run_id.slice(0, 8)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="cln-summary-actions">
            {run && (
              <>
                <span className={`cln-badge ${statusBadgeClass(run.status)}`}>{run.status}</span>
                {run.cap_exceeded && (
                  <span className="cln-badge cln-badge--honey">NEEDS REVIEW</span>
                )}
              </>
            )}
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh">
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run?.error_message && <div className="cln-summary-error">{run.error_message}</div>}
      </section>

      {run && (
        <section className="cln-summary-card">
          <div className="cln-summary-grid">
            <SummaryStat label="Due" value={run.records_due} variant="ink" />
            <SummaryStat label="Eligible" value={run.records_eligible} />
            <SummaryStat
              label="Quarantined"
              value={run.records_quarantined}
              variant={run.records_quarantined > 0 ? 'honey' : 'muted'}
            />
            <SummaryStat label="Removed" value={run.records_removed} variant="sage" />
            <SummaryStat
              label="Failed"
              value={run.records_failed}
              variant={run.records_failed > 0 ? 'rose' : 'muted'}
            />
            <SummaryStat label="Domains" value={run.domains_total} />
            <SummaryStat label="Domains deleted" value={removal?.domains_deleted ?? 0} />
            <SummaryStat label="Users removed" value={removal?.users_deleted ?? 0} />
          </div>
        </section>
      )}

      {isMicrosoft ? (
        <MsItemsSection items={msItems} />
      ) : (
      <section className="cln-card">
        <div className="cln-card-header">
          <h2 className="cln-card-title">Records ({records.length})</h2>
        </div>

        {records.length === 0 && (
          <div className="cln-empty">
            <span>No records in this run.</span>
          </div>
        )}

        {records.length > 0 && (
          <table className="cln-table">
            <thead>
              <tr>
                <th>Record</th>
                <th>Outcome</th>
                <th>Reason</th>
                <th>Source</th>
                <th className="cln-col-num"># Domains</th>
                <th>Links</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((rec) => {
                const isBad = rec.outcome === 'failed' || rec.outcome === 'quarantined';
                const diff = rec.clean_vs_sheet_diff;
                const hasDiff =
                  !!diff &&
                  ((diff.only_in_sheet?.length ?? 0) > 0 || (diff.only_in_clean?.length ?? 0) > 0);
                return (
                  <tr key={rec.id} className={isBad ? 'cln-row--bad' : undefined}>
                    <td className="cln-cell-mono">
                      <a
                        href={`${AIRTABLE_RECORD_BASE}/${rec.airtable_record_id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={linkStyle}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {rec.airtable_record_id}
                      </a>
                    </td>
                    <td>
                      <span className={`cln-badge ${outcomeBadgeClass(rec.outcome)}`}>
                        {rec.outcome}
                      </span>
                    </td>
                    <td className="cln-cell-faint">{rec.reason ?? '-'}</td>
                    <td className="cln-cell-faint">{rec.domain_source ?? '-'}</td>
                    <td className="cln-col-num">
                      {rec.domains.length > 0 ? (
                        <details>
                          <summary style={{ cursor: 'pointer' }}>{rec.domains.length}</summary>
                          <DomainList domains={rec.domains} diff={hasDiff ? diff : null} />
                        </details>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="cln-cell-faint">
                      {rec.new_sheet_url && (
                        <a
                          href={rec.new_sheet_url}
                          target="_blank"
                          rel="noreferrer"
                          style={linkStyle}
                        >
                          NEW_SHEET
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      )}
    </>
  );
}

/* ─── MICROSOFT per-domain items section ─────────────────── */

function msItemBadgeClass(status: string): string {
  if (status === 'deleted') return 'cln-badge--success';
  if (status === 'failed') return 'cln-badge--error';
  return 'cln-badge--muted';
}

function MsItemsSection({ items }: { items: MsItem[] | null }): JSX.Element {
  return (
    <section className="cln-card">
      <div className="cln-card-header">
        <h2 className="cln-card-title">Domains ({items?.length ?? 0})</h2>
      </div>

      {items === null && <div className="cln-empty">Loading domains...</div>}

      {items !== null && items.length === 0 && (
        <div className="cln-empty">
          <span>No domains in this run.</span>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <table className="cln-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th className="cln-col-num">Tenants found</th>
              <th className="cln-col-num">Users deleted</th>
              <th>Domain deleted</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isBad = it.status === 'failed';
              return (
                <tr key={it.domain_name} className={isBad ? 'cln-row--bad' : undefined}>
                  <td className="cln-cell-mono">{it.domain_name}</td>
                  <td className="cln-col-num">{it.tenants_found ?? '-'}</td>
                  <td className="cln-col-num">{it.users_deleted ?? '-'}</td>
                  <td>
                    <span
                      className={`cln-badge ${
                        it.domain_deleted ? 'cln-badge--success' : 'cln-badge--muted'
                      }`}
                    >
                      {it.domain_deleted ? 'yes' : 'no'}
                    </span>
                    {it.default_domain_blocked && (
                      <span className="cln-badge cln-badge--honey" style={{ marginLeft: 6 }}>
                        DEFAULT BLOCKED
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`cln-badge ${msItemBadgeClass(it.status)}`}>{it.status}</span>
                  </td>
                  <td className="cln-cell-faint">{it.error ?? '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function DomainList({
  domains,
  diff,
}: {
  domains: string[];
  diff: { only_in_sheet: string[]; only_in_clean: string[] } | null;
}): JSX.Element {
  return (
    <div style={{ marginTop: 8, textAlign: 'left' }}>
      <div className="cln-cell-mono" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
        {domains.join('\n')}
      </div>
      {diff && (
        <div style={{ marginTop: 8 }}>
          {diff.only_in_sheet.length > 0 && (
            <div className="cln-cell-faint" style={{ fontSize: 12 }}>
              <strong>Only in sheet ({diff.only_in_sheet.length}):</strong>{' '}
              <span className="cln-cell-mono">{diff.only_in_sheet.join(', ')}</span>
            </div>
          )}
          {diff.only_in_clean.length > 0 && (
            <div className="cln-cell-faint" style={{ fontSize: 12 }}>
              <strong>Only in clean ({diff.only_in_clean.length}):</strong>{' '}
              <span className="cln-cell-mono">{diff.only_in_clean.join(', ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── SummaryStat (inline-copied from DomainRemoval) ─────── */

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted' | 'ink';
  hint?: string;
}): JSX.Element {
  return (
    <div className="cln-summary-stat">
      <span className="cln-summary-stat-label">{label}</span>
      <span className={`cln-summary-stat-value cln-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="cln-summary-stat-hint">{hint}</span>}
    </div>
  );
}
